"use client";
import { Button } from "@/components/ui/button-pro";
import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
import { PiCaretLeftBold, PiCaretRightBold } from "react-icons/pi";
export function ImagesCarouselNew({
  images,
  onClickCallback,
  hideSwiperBtns,
  firstThumbnail,
  title,
}: {
  images: string[];
  title?: string;
  hideSwiperBtns?: boolean;
  geometry?: { width: number; height: number };
  onClickCallback?: () => void;
  firstThumbnail?: string;
}) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [carouselDirection, setCarouselDirection] = useState<"left" | "right">(
    "right"
  );
  const [showCarousel, setShowCarousel] = useState(
    firstThumbnail ? false : true
  );
  const [inView, setInView] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleNextImage = () => {
    if (images.length <= 1) return;
    if (showCarousel === false) setShowCarousel(true);

    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    setCarouselDirection("right");
    resetAutoAdvance();
  };

  const handlePreviousImage = () => {
    if (images.length <= 1) return;
    if (showCarousel === false) setShowCarousel(true);

    setCurrentImageIndex((prevIndex) => {
      if (prevIndex <= 0) return images.length - 1;
      return prevIndex - 1;
    });
    setCarouselDirection("left");
    resetAutoAdvance();
  };

  const resetAutoAdvance = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (!isHovered && images.length > 1 && showCarousel && inView) {
      intervalRef.current = setInterval(() => {
        handleNextImage();
      }, 3000);
    }
  };

  // Show the first thumbnail for 3 seconds when entering the viewport,
  // then start the carousel (auto-advance) only while in view.
  useEffect(() => {
    const el = imageContainerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            // If we have a first thumbnail, show it for 3 seconds before starting the carousel
            if (firstThumbnail) {
              setShowCarousel(false);
              if (intervalRef.current) clearInterval(intervalRef.current);
              if (timeoutRef.current) clearTimeout(timeoutRef.current);
              timeoutRef.current = setTimeout(() => {
                setShowCarousel(true);
                resetAutoAdvance();
              }, 3000);
            } else {
              // No first thumbnail: can start auto-advance immediately
              setShowCarousel(true);
              resetAutoAdvance();
            }
          } else {
            setInView(false);
            // Pause when out of view
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
          }
        });
      },
      { threshold: 0.3 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [firstThumbnail]);

  useEffect(() => {
    if (images.length < 2) return;
    if (!isHovered && showCarousel && inView) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        handleNextImage();
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [images.length, isHovered, showCarousel, inView]);

  const handleImageClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!imageContainerRef.current || images.length <= 1) {
      onClickCallback?.();
      return;
    }

    const clickX = event.clientX;
    const imageWidth = imageContainerRef.current.clientWidth;

    if (clickX < imageWidth / 2) {
      handlePreviousImage();
    } else {
      handleNextImage();
    }
    onClickCallback?.();
  };

  const handleImageError = () => {
    //   setError("Failed to load image");
    setImageError(false);
    setIsLoading(false);
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setError(null);
    setImageError(false);
  };

  useEffect(() => {
    setImageError(false);
  }, [currentImageIndex]);

  if (images.length === 0 && !firstThumbnail) {
    return (
      <div className="flex h-[200px] sm:h-[250px] md:h-[300px] lg:h-[400px] items-center justify-center rounded-lg bg-slate-600">
        <p className="text-black dark:text-white">No images available</p>
      </div>
    );
  }

  return (
    <>
      <div
        className="group w-full h-[200px] sm:h-[250px] md:h-[300px] lg:h-[400px] overflow-hidden rounded-lg bg-slate-300 dark:bg-slate-800"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        ref={imageContainerRef}
      >
        <div
          onClick={handleImageClick}
          onDoubleClick={() => {
            onClickCallback?.();
          }}
          className="overflow-hidden cursor-pointer"
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-700">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-t-transparent dark:border-accent-green"></div>
            </div>
          )}

          {error && (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-black dark:text-white">{error}</p>
            </div>
          )}

          {/* <div className='absolute inset-0 dark:bg-gradient-to-t dark:from-gray-400 dark:to-transparent opacity-60'></div> */}

          <Image
            unoptimized
            src={imageError ? "/placeholder.svg" : images[currentImageIndex]}
            alt={`Image ${currentImageIndex + 1} of ${images.length}`}
            fill
            className="object-cover transition-opacity duration-300 aspect-square"
            // onError={() => setImageError(true)}
            onError={handleImageError}
            onLoad={handleImageLoad}
            priority={currentImageIndex === 0}
          />

          {/* {firstThumbnail && !showCarousel && ( */}
          {firstThumbnail && (
            <div className="relative h-full w-full">
              <Image
                unoptimized
                src={firstThumbnail}
                fill
                className="object-cover absolute z-10 transition-opacity ease-in-out shadow-lg hover:shadow-purple-400 duration-300 aspect-square"
                alt="project first thumbnail"
                // alt={`Image ${currentImageIndex + 1} of ${project.length}`}
                // className='z-10 absolute h-full w-full object-cover transition-opacity duration-300'
              />
              {title && (
                <div className="z-20 absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <div className="p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{title}</h3>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* {loadingClickedProject[currentImageIndex] && (
            <FuturisticLoadingSpinner />
          )} */}
        </div>

        {images.length > 1 && !hideSwiperBtns && (
          <div className="absolute bottom-1/2 left-0 right-0 z-50 flex justify-between gap-2 w-full px-2">
            <Button
              variant={"outline"}
              onClick={handlePreviousImage}
              aria-label="Previous image"
              size={"sm"}
              className={cn(
                "bg-gray-200 dark:bg-gray-200 text-blue-800 hover:text-black transition-all border",
                carouselDirection === "left" &&
                  "bg-dark dark:bg-black text-white hover:text-white"
                // 'bg-gray-200 dark:bg-gray-700/70 text-foreground'
              )}
            >
              <PiCaretLeftBold
                className={cn(
                  "flex items-center justify-center rounded-sm text-[44px]"
                )}
              />
            </Button>
            <Button
              variant={"outline"}
              onClick={handleNextImage}
              size={"sm"}
              aria-label="Next image"
              className={cn(
                "bg-gray-200 dark:bg-gray-200 text-blue-800 hover:text-black transition-all border",
                carouselDirection === "right" &&
                  "bg-dark dark:bg-black text-white hover:text-white"
              )}
            >
              <PiCaretRightBold
                className={cn(
                  "flex items-center justify-center rounded-sm text-[44px]"
                )}
              />
            </Button>
          </div>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-2 right-2 z-50 rounded-full bg-gray-300 text-black/80 px-2 py-1 text-sm dark:bg-gray-700 dark:text-white">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </>
  );
}
